import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Shared component overrides for consistent design tokens
const sharedComponents = {
  MuiPaper: {
    defaultProps: { elevation: 0 },
    styleOverrides: {
      root: { borderRadius: 12 },
      elevation0: { boxShadow: '0 1px 3px rgba(0,0,0,0.08)' },
    },
  },
  MuiCard: {
    defaultProps: { elevation: 0 },
    styleOverrides: {
      root: { borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' },
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: { borderRadius: 16 },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: { borderRadius: 8, textTransform: 'none', fontWeight: 500 },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: { borderRadius: 8 },
    },
  },
  MuiTableContainer: {
    styleOverrides: {
      root: { borderRadius: 12 },
    },
  },
};

// Light theme configuration
const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#667eea',
    },
    secondary: {
      main: '#764ba2',
    },
    background: {
      default: '#fafbfc',
      paper: '#ffffff',
    },
    divider: 'rgba(0,0,0,0.06)',
  },
  shape: { borderRadius: 12 },
  components: sharedComponents,
});

// Dark theme configuration
const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#90caf9',
    },
    secondary: {
      main: '#f48fb1',
    },
    background: {
      default: '#0f1117',
      paper: '#1a1d28',
    },
    divider: 'rgba(255,255,255,0.08)',
  },
  shape: { borderRadius: 12 },
  components: {
    ...sharedComponents,
    MuiPaper: {
      ...sharedComponents.MuiPaper,
      styleOverrides: {
        ...sharedComponents.MuiPaper.styleOverrides,
        elevation0: { boxShadow: '0 1px 3px rgba(0,0,0,0.3)' },
      },
    },
    MuiCard: {
      ...sharedComponents.MuiCard,
      styleOverrides: {
        ...sharedComponents.MuiCard.styleOverrides,
        root: { borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.3)' },
      },
    },
  },
});

export const ThemeProvider = ({ children }) => {
  const [themeMode, setThemeMode] = useState(() => {
    // Get from localStorage first, then default to 'light'
    const stored = localStorage.getItem('theme');
    return stored || 'light';
  });

  // Save to localStorage whenever theme changes
  useEffect(() => {
    localStorage.setItem('theme', themeMode);
  }, [themeMode]);

  const toggleTheme = () => {
    setThemeMode(prev => prev === 'light' ? 'dark' : 'light');
  };

  const setTheme = (mode) => {
    if (mode === 'light' || mode === 'dark') {
      setThemeMode(mode);
    }
  };

  const theme = useMemo(() => {
    return themeMode === 'dark' ? darkTheme : lightTheme;
  }, [themeMode]);

  const value = {
    themeMode,
    toggleTheme,
    setTheme,
    isDark: themeMode === 'dark',
  };

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
